/**
 * Custom hook for booking appointments
 */

import { useState, useCallback } from 'react';
import type { AppointmentRequestResponse } from '@/types';
import { createAppointmentRequest } from '@/services/widgetApi';

interface BookingData {
  practiceId: string;
  serviceId: string;
  clinicianId: string;
  locationId: string;
  paymentMethodId?: string;
  requestedDate: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  appointmentType: 'individual' | 'couple' | 'group';
  locationType: 'in_person' | 'video' | 'phone';
  patientInfo: {
    email: string;
    phone: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string; // YYYY-MM-DD
    sex: 'male' | 'female' | 'non_binary' | 'prefer_not_to_say';
    notes?: string;
  };
}

interface UseAppointmentBookingResult {
  booking: AppointmentRequestResponse | null;
  loading: boolean;
  error: string | null;
  bookAppointment: (data: BookingData) => Promise<void>;
  resetBooking: () => void;
}

/**
 * Hook to create appointment requests
 */
export function useAppointmentBooking(): UseAppointmentBookingResult {
  const [booking, setBooking] = useState<AppointmentRequestResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const bookAppointment = useCallback(async (data: BookingData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await createAppointmentRequest({
        practice_id: data.practiceId,
        appointment_request: {
          practice_service_id: data.serviceId,
          clinician_id: data.clinicianId,
          location_id: data.locationId,
          payment_method_id: data.paymentMethodId,
          requested_date: data.requestedDate,
          start_time: data.startTime,
          end_time: data.endTime,
          appointment_type: data.appointmentType,
          location_type: data.locationType,
          prospect_email: data.patientInfo.email,
          prospect_phone: data.patientInfo.phone,
          prospect_first_name: data.patientInfo.firstName,
          prospect_last_name: data.patientInfo.lastName,
          prospect_date_of_birth: data.patientInfo.dateOfBirth,
          prospect_sex: data.patientInfo.sex,
          patient_notes: data.patientInfo.notes,
        },
      });

      if (response.success) {
        setBooking(response);
      } else {
        setError('Failed to create appointment request');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to book appointment';
      setError(errorMessage);
      console.error('Error in useAppointmentBooking:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const resetBooking = useCallback(() => {
    setBooking(null);
    setError(null);
  }, []);

  return {
    booking,
    loading,
    error,
    bookAppointment,
    resetBooking,
  };
}
